import { LoginRequest, LoginResponse, SignupRequest, User, roleUtils } from '../types/auth.types';
import api from './api.service';
import { jwtDecode } from 'jwt-decode';

// Type for JWT payload
interface JwtPayload {
  sub: string;
  exp: number;
  iat: number;
  roles: string[];
}

/**
 * Authentication service for handling login, signup and token management
 */
const authService = {
  /**
   * Login user with username and password
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    
    // Store tokens in localStorage for future requests
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    
    return response.data;
  },
  
  /**
   * Signup a new user
   */
  signup: async (signupData: SignupRequest): Promise<User> => {
    const response = await api.post<User>('/auth/signup', signupData);
    return response.data;
  },
  
  /**
   * Logout user by removing tokens from localStorage
   */
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  },
  
  /**
   * Check if user is logged in by verifying token existence and validity
   */
  isLoggedIn: (): boolean => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const currentTime = Date.now() / 1000;
      
      return decoded.exp > currentTime;
    } catch (error) {
      // If token is invalid, remove it
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      return false;
    }
  },
  
  /**
   * Get current user information from token
   */
  getCurrentUser: (): User | null => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      
      // Check if token is expired
      const currentTime = Date.now() / 1000;
      if (decoded.exp <= currentTime) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        return null;
      }
      
      // Basic user info from token
      return {
        id: parseInt(decoded.sub),
        username: decoded.sub,
        email: '', // Email not available in token
        roles: decoded.roles
      };
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      return null;
    }
  },
  
  /**
   * Get user roles from token
   */
  getUserRoles: (): string[] => {
    const user = authService.getCurrentUser();
    return user ? roleUtils.getRoleNames(user.roles) : [];
  },
  
  /**
   * Check if user has specific role
   */
  hasRole: (role: string): boolean => {
    const roles = authService.getUserRoles();
    return roles.includes(role);
  }
};

export default authService; 